import { parseHttpUrl } from "@/blocks/ProductGallery/media-url";
/**
 * Muted inline preview for direct-file video slides of the product gallery.
 *
 * The video uses the browser's native controls (same as the lightbox player), so the user can pause,
 * seek or unmute it without any custom UI.
 *
 * Performance contract (keep in sync with the markup in `_ProductHero.cshtml`):
 * - The server renders a preview only for direct-file videos (it classifies embeds), so no host check happens here.
 * - Server markup has no `src` and `preload="none"`: nothing is downloaded until a preview starts.
 * - At most one preview is ever active, and only the active slide's preview is attached to a source.
 * - Nothing starts before `load` + idle, while the carousel is off-screen, the tab is hidden, the lightbox
 *   is open, or when the user prefers reduced motion / saves data / is on a slow connection.
 * - Autoplay is capped at `MAX_PLAYS` loops; after that the user can replay with the native controls.
 * - A pause made by the user (native controls) is respected until the slide is left.
 */
const MAX_PLAYS = 3;
const VISIBILITY_THRESHOLD = 0.5;
const IDLE_TIMEOUT_MS = 2000;
const SLOW_CONNECTION_TYPES = new Set(["slow-2g", "2g", "3g"]);
const NOOP_CONTROLLER = {
    setActive: () => undefined,
    setLightboxOpen: () => undefined,
    destroy: () => undefined,
};
function canAutoplay(reducedMotion) {
    if (reducedMotion.matches) {
        return false;
    }
    const connection = navigator.connection;
    if (connection?.saveData) {
        return false;
    }
    return !(connection?.effectiveType && SLOW_CONNECTION_TYPES.has(connection.effectiveType));
}
/** Subscribes to media query changes; Safari < 14 only has the legacy `addListener` API. Returns the unsubscribe. */
function onMediaQueryChange(query, listener) {
    if (typeof query.addEventListener === "function") {
        query.addEventListener("change", listener);
        return () => query.removeEventListener("change", listener);
    }
    query.addListener(listener);
    return () => query.removeListener(listener);
}
/** Runs `callback` once the page has loaded (so the LCP poster is done) and the main thread is idle. */
function whenLoadedAndIdle(callback) {
    let idleHandle;
    let timeoutHandle;
    let cancelled = false;
    const schedule = () => {
        if (cancelled) {
            return;
        }
        if (typeof window.requestIdleCallback === "function") {
            idleHandle = window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
        }
        else {
            timeoutHandle = window.setTimeout(callback, 1000);
        }
    };
    if (document.readyState === "complete") {
        schedule();
    }
    else {
        window.addEventListener("load", schedule, { once: true });
    }
    return () => {
        cancelled = true;
        window.removeEventListener("load", schedule);
        if (idleHandle !== undefined && typeof window.cancelIdleCallback === "function") {
            window.cancelIdleCallback(idleHandle);
        }
        if (timeoutHandle !== undefined) {
            window.clearTimeout(timeoutHandle);
        }
    };
}
export function createVideoPreviewController(slides, observedRoot) {
    const previews = new Map();
    /** Pauses the video on our own initiative (not the user's). */
    const pauseBySystem = (preview) => {
        if (!preview.video.paused) {
            preview.systemPause = true;
            preview.video.pause();
        }
    };
    /** Stops playback and detaches the source so the buffer and decoder are freed. */
    const release = (preview) => {
        const { video } = preview;
        pauseBySystem(preview);
        if (video.hasAttribute("src")) {
            video.removeAttribute("src");
            video.load();
        }
        // load() drops the element's queued events, including the `pause` that would have cleared this flag.
        preview.systemPause = false;
        video.dataset.playing = "false";
    };
    /** Releases a preview and forgets everything learned about it (used when its slide is left). */
    const leave = (preview) => {
        release(preview);
        preview.plays = 0;
        preview.blocked = false;
        preview.userPaused = false;
        preview.exhausted = false;
    };
    const play = (preview) => {
        preview.video.play().catch((error) => {
            // An AbortError means we paused/released while play() was pending; anything else (e.g. autoplay
            // blocked) is not retried: the poster simply stays visible.
            if (error instanceof DOMException && error.name === "AbortError") {
                return;
            }
            preview.blocked = true;
            release(preview);
        });
    };
    const start = (preview) => {
        const { video } = preview;
        previews.forEach((other) => {
            if (other !== preview) {
                pauseBySystem(other);
            }
        });
        if (!video.hasAttribute("src")) {
            const parsed = parseHttpUrl(video.dataset.previewSrc ?? "");
            if (!parsed) {
                preview.blocked = true;
                return;
            }
            video.src = parsed.toString();
        }
        play(preview);
    };
    slides.forEach((slide, index) => {
        const video = slide.querySelector("[data-preview-video]");
        if (!video) {
            return;
        }
        const preview = {
            video,
            plays: 0,
            blocked: false,
            userPaused: false,
            exhausted: false,
            systemPause: false,
            dispose: () => undefined,
        };
        const onPlaying = () => {
            video.dataset.playing = "true";
        };
        const onPlay = () => {
            // Playback started by the user through the native controls clears their earlier pause, and replaying
            // after the autoplay cap starts a fresh cycle.
            preview.userPaused = false;
            if (preview.exhausted) {
                preview.exhausted = false;
                preview.plays = 0;
            }
        };
        const onPause = () => {
            if (preview.systemPause) {
                preview.systemPause = false;
                return;
            }
            // `pause` also fires when playback ends and when the source is detached; neither is a user pause.
            if (!video.ended && video.hasAttribute("src")) {
                preview.userPaused = true;
            }
        };
        const onEmptied = () => {
            video.dataset.playing = "false";
        };
        const onEnded = () => {
            preview.plays += 1;
            if (preview.plays < MAX_PLAYS) {
                video.currentTime = 0;
                play(preview);
                return;
            }
            // Cap reached: stop on the last frame; the native controls can replay it.
            preview.exhausted = true;
        };
        const onError = () => {
            preview.blocked = true;
            release(preview);
        };
        video.addEventListener("playing", onPlaying);
        video.addEventListener("play", onPlay);
        video.addEventListener("pause", onPause);
        video.addEventListener("emptied", onEmptied);
        video.addEventListener("ended", onEnded);
        video.addEventListener("error", onError);
        preview.dispose = () => {
            video.removeEventListener("playing", onPlaying);
            video.removeEventListener("play", onPlay);
            video.removeEventListener("pause", onPause);
            video.removeEventListener("emptied", onEmptied);
            video.removeEventListener("ended", onEnded);
            video.removeEventListener("error", onError);
        };
        previews.set(index, preview);
    });
    if (previews.size === 0) {
        return NOOP_CONTROLLER;
    }
    let activeIndex = -1;
    let ready = false;
    let visible = typeof IntersectionObserver === "undefined";
    let lightboxOpen = false;
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
        const eligible = ready && visible && !lightboxOpen && !document.hidden && canAutoplay(motionQuery);
        previews.forEach((preview, index) => {
            if (index !== activeIndex) {
                leave(preview);
                return;
            }
            if (lightboxOpen) {
                release(preview);
                return;
            }
            const shouldPlay = eligible && !preview.blocked && !preview.userPaused && !preview.exhausted;
            if (shouldPlay) {
                if (preview.video.paused) {
                    start(preview);
                }
            }
            else {
                pauseBySystem(preview);
            }
        });
    };
    const cancelIdle = whenLoadedAndIdle(() => {
        ready = true;
        sync();
    });
    const observer = typeof IntersectionObserver === "undefined"
        ? undefined
        : new IntersectionObserver((entries) => {
            const latest = entries[entries.length - 1];
            if (latest) {
                visible = latest.isIntersecting;
                sync();
            }
        }, { threshold: VISIBILITY_THRESHOLD });
    observer?.observe(observedRoot);
    document.addEventListener("visibilitychange", sync);
    const unsubscribeMotion = onMediaQueryChange(motionQuery, sync);
    return {
        setActive: (index) => {
            activeIndex = index;
            sync();
        },
        setLightboxOpen: (open) => {
            lightboxOpen = open;
            sync();
        },
        destroy: () => {
            cancelIdle();
            observer?.disconnect();
            document.removeEventListener("visibilitychange", sync);
            unsubscribeMotion();
            previews.forEach((preview) => {
                preview.dispose();
                leave(preview);
            });
        },
    };
}
